// Audit › Changes: who changed what, across every tenant, with the value
// before and after.
//
// Filters (all in the URL): league_id, actor_id, entity_type, action,
// from / to (inclusive, read in the viewer's zone via `tz`), q (debounced),
// staff=hide. Reset clears the URL, and every control follows the URL.
// Paging is by cursor. CSV comes from the server for the whole range.

import { useId, useMemo } from 'react'
import { api } from '@/api'
import { isApiError } from '@/api/client'
import type { AuditListParams, AuditListResponse } from '@/api/types'
import { AuditEntryList } from '@/components/audit/AuditEntryList'
import { DateRangeFields, ExportCsvButton, TenantSelect } from '@/components/audit/FilterControls'
import { dateParam, idParam, useUrlFilters } from '@/components/audit/urlFilters'
import { useCursorPages } from '@/components/audit/useCursorPages'
import { Button, Card, EmptyState, ErrorState, FormField, SearchInput, SelectInput, SkeletonRows, Toggle } from '@/components/ui'
import { AUDIT_ACTIONS, AUDIT_ENTITY_TYPES } from '@/lib/audit'
import { titleCase, viewerTimeZone } from '@/lib/format'
import { useQuery } from '@/lib/query'

const KEYS = ['league_id', 'actor_id', 'entity_type', 'action', 'from', 'to', 'q', 'staff'] as const
const PAGE = 50

export default function ChangesTab() {
  const { values: f, set, reset, active } = useUrlFilters(KEYS)
  const staffId = useId()
  const rangeError = f.from && f.to && f.from > f.to ? 'The end date is before the start date.' : null

  const params = useMemo<AuditListParams>(() => {
    const from = dateParam(f.from)
    const to = dateParam(f.to)
    return {
      league_id: idParam(f.league_id),
      actor_id: idParam(f.actor_id),
      entity_type: f.entity_type || undefined,
      action: f.action || undefined,
      from,
      to,
      tz: from || to ? viewerTimeZone() : undefined,
      q: f.q.trim() || undefined,
      exclude_super_admin: f.staff === 'hide' || undefined,
    }
  }, [f.league_id, f.actor_id, f.entity_type, f.action, f.from, f.to, f.q, f.staff])

  const { first, entries, nextCursor, loadMore, loadingMore, moreError } = useCursorPages<AuditListResponse['entries'][number], AuditListResponse>(
    rangeError ? null : ['audit', 'changes', params],
    (cursor, signal) => api.audit.list({ ...params, limit: PAGE, cursor }, signal),
  )

  const badFilter = first.status === 'error' && isApiError(first.error) && first.error.status === 400

  return (
    <div className="grid grid-cols-1 gap-4">
      <div className="grid gap-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
          <SearchInput label="Search changes" placeholder="Search summary, entity or person's email" value={f.q} onChange={(v) => set({ q: v })} className="sm:max-w-md sm:flex-1" />
          <div className="flex flex-wrap items-start gap-2 sm:ml-auto">
            <Button onClick={reset} disabled={!active}>
              Reset
            </Button>
            <ExportCsvButton run={() => api.audit.export(params)} />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6">
          <div className="lg:col-span-2">
            <TenantSelect value={f.league_id} onChange={(v) => set({ league_id: v })} />
          </div>
          <div className="lg:col-span-2">
            <PersonSelect value={f.actor_id} onChange={(v) => set({ actor_id: v })} />
          </div>
          <FormField label="Entity">
            {({ id }) => (
              <SelectInput
                id={id}
                value={f.entity_type}
                options={[{ value: '', label: 'Any entity' }, ...AUDIT_ENTITY_TYPES.map((t) => ({ value: t, label: titleCase(t) })), ...(f.entity_type && !(AUDIT_ENTITY_TYPES as readonly string[]).includes(f.entity_type) ? [{ value: f.entity_type, label: f.entity_type }] : [])]}
                onChange={(e) => set({ entity_type: e.target.value })}
              />
            )}
          </FormField>
          <FormField label="Action">
            {({ id }) => (
              <SelectInput
                id={id}
                value={f.action}
                options={[{ value: '', label: 'Any action' }, ...AUDIT_ACTIONS.map((a) => ({ value: a, label: titleCase(a) }))]}
                onChange={(e) => set({ action: e.target.value })}
              />
            )}
          </FormField>
          <div className="grid grid-cols-1 gap-3 sm:col-span-2 sm:grid-cols-2 lg:col-span-4">
            <DateRangeFields from={f.from} to={f.to} error={rangeError} onChange={(p) => set(p)} />
          </div>
          <div className="flex items-center gap-2.5 sm:col-span-2 lg:items-end lg:pb-2">
            <Toggle size="sm" checked={f.staff === 'hide'} onChange={(v) => set({ staff: v ? 'hide' : '' })} labelledBy={staffId} />
            <span id={staffId} className="text-[13px] text-ink">
              Hide ScoreLM staff
            </span>
          </div>
        </div>
      </div>

      <Card flush className="overflow-hidden">
        {rangeError ? (
          <EmptyState title="Check the dates" description={rangeError} action={<Button size="sm" onClick={() => set({ from: '', to: '' })}>Clear dates</Button>} />
        ) : first.status === 'loading' && !first.data ? (
          <SkeletonRows rows={6} label="Loading changes" />
        ) : first.status === 'error' && !first.data ? (
          <div>
            <ErrorState error={first.error} title={badFilter ? "Those filters aren't valid" : "Couldn't load changes"} onRetry={badFilter ? undefined : first.refetch} />
            {badFilter ? (
              <div className="-mt-8 flex justify-center pb-10">
                <Button size="sm" onClick={reset}>
                  Reset filters
                </Button>
              </div>
            ) : null}
          </div>
        ) : entries.length === 0 ? (
          <EmptyState
            title={active ? 'No changes match' : 'No changes yet'}
            description={active ? 'Nothing matches these filters. Widen the dates or clear a filter.' : 'Changes appear here as soon as anyone edits anything.'}
            action={active ? <Button size="sm" onClick={reset}>Reset filters</Button> : undefined}
          />
        ) : (
          <>
            {first.status === 'error' ? (
              <div role="alert" className="flex flex-wrap items-center justify-between gap-2 border-b border-line bg-bad-soft/40 px-4 py-2 text-xs text-bad">
                Couldn&apos;t refresh. Showing the last results.
                <Button size="sm" variant="ghost" onClick={first.refetch}>
                  Retry
                </Button>
              </div>
            ) : null}
            <div className={first.isFetching ? 'opacity-60' : undefined} aria-busy={first.isFetching || undefined}>
              <AuditEntryList entries={entries} label="Changes" />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-4 py-3 sm:px-5">
              <p className="text-xs text-muted tabular">
                {entries.length} shown{nextCursor == null ? ', that’s everything that matches' : ''}
              </p>
              {moreError ? (
                <p role="alert" className="text-xs text-bad">
                  Couldn&apos;t load more: {moreError}
                </p>
              ) : null}
              {nextCursor != null ? (
                <Button size="sm" onClick={loadMore} loading={loadingMore}>
                  Load more
                </Button>
              ) : null}
            </div>
          </>
        )}
      </Card>
    </div>
  )
}

/** Person picker: super-admins and league admins, by name. */
function PersonSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { data, status } = useQuery(['users', 'picker'], (signal) => api.users.list({ page_size: 100 }, signal))
  const people = (data?.users ?? []).filter((u) => u.platform_role === 'super_admin' || u.memberships.length > 0)
  const options = [
    { value: '', label: status === 'loading' ? 'Loading people…' : 'Anyone' },
    ...people.map((u) => ({ value: String(u.id), label: u.name ? `${u.name} (${u.email})` : u.email })),
  ]
  if (value && !options.some((o) => o.value === value)) options.push({ value, label: `User #${value}` })
  return (
    <FormField label="Person">
      {({ id }) => <SelectInput id={id} value={value} options={options} onChange={(e) => onChange(e.target.value)} />}
    </FormField>
  )
}
